import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Bot, CalendarDays, LayoutGrid, LifeBuoy, LogOut, Plus, Search, Settings, SquareChartGantt, MessageSquareQuote, Sparkles } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import { WorkspaceSettingsModal } from './WorkspaceSettingsModal';
import { ensureCrispLoaded, openCrispChat } from '../lib/crisp';
import { supabase } from '../lib/supabase';
import { WORKSPACE_FIELDS } from '../lib/workspaces';
import { useToast } from './Toast';
import type { Workspace } from '../types/database';
import { PlanLimits } from './PlanLimits';
import { FOUNDER_CALL_URL, openFounderCall } from '../lib/founderCall';

// Panel toggle icons
const PanelCloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="18" height="18" x="3" y="3" rx="2"/>
    <path d="M9 3v18"/>
    <path d="m16 15-3-3 3-3"/>
  </svg>
);

const PanelOpenIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'scaleX(-1)' }}>
    <rect width="18" height="18" x="3" y="3" rx="2"/>
    <path d="M9 3v18"/>
    <path d="m16 15-3-3 3-3"/>
  </svg>
);

const CompetitorsNavIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12c.552 0 1.005-.449.95-.998a10 10 0 0 0-8.953-8.951c-.55-.055-.998.398-.998.95v8a1 1 0 0 0 1 1z"/>
    <path d="M21.21 15.89A10 10 0 1 1 8 2.83"/>
  </svg>
);

interface SidebarProps {
  onOpenAccount?: () => void;
  showFounderCallLink?: boolean;
}

const hasValidAvatar = (url: string | null | undefined): boolean => {
  if (!url) return false;
  if (url.includes('supabase') && url.includes('/storage/')) return true;
  if (url.includes('googleusercontent.com')) return true;
  if (url.includes('githubusercontent.com')) return true;
  if (url.includes('platform-lookaside.fbsbx.com')) return true;
  if (url.includes('pbs.twimg.com')) return true;
  return false;
};

export function Sidebar({ onOpenAccount, showFounderCallLink }: SidebarProps) {
  const { user, profile, workspace, viewingAs, signOut } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  
  const [isWorkspaceDropdownOpen, setIsWorkspaceDropdownOpen] = useState(false);
  const [isWorkspaceSettingsOpen, setIsWorkspaceSettingsOpen] = useState(false);
  const [settingsWorkspace, setSettingsWorkspace] = useState<Workspace | null>(null);
  const workspaceDropdownRef = useRef<HTMLDivElement>(null);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Persist collapse state in localStorage
    const saved = localStorage.getItem('sidebar_collapsed');
    return saved === 'true';
  });
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 991 : false
  );
  const [showExpandedContent, setShowExpandedContent] = useState(() => {
    const saved = localStorage.getItem('sidebar_collapsed');
    return saved !== 'true';
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    ensureCrispLoaded();
  }, []);

  // Save collapse state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', String(isCollapsed));
  }, [isCollapsed]);

  useEffect(() => {
    const updateViewportState = () => {
      setIsMobileViewport(window.innerWidth <= 991);
    };

    updateViewportState();
    window.addEventListener('resize', updateViewportState);
    return () => window.removeEventListener('resize', updateViewportState);
  }, []);

  const effectiveCollapsed = isCollapsed && !isMobileViewport;

  useEffect(() => {
    if (isMobileViewport) {
      setShowExpandedContent(true);
      return;
    }

    if (effectiveCollapsed) {
      setShowExpandedContent(false);
      return;
    }

    const timer = window.setTimeout(() => {
      setShowExpandedContent(true);
    }, 85);

    return () => window.clearTimeout(timer);
  }, [effectiveCollapsed, isMobileViewport]);

  // Close mobile menu when clicking outside or navigating
  useEffect(() => {
    const handleResize = () => {
      // Close mobile menu when resizing to desktop
      if (window.innerWidth > 991) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu on navigation
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [navigate]);

  // Toggle body scroll lock when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('mobile-menu-open');
    };
  }, [isMobileMenuOpen]);


  // Close workspace dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (workspaceDropdownRef.current && !workspaceDropdownRef.current.contains(event.target as Node)) {
        setIsWorkspaceDropdownOpen(false);
      }
    };
    if (isWorkspaceDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isWorkspaceDropdownOpen]);

  // Fetch all workspaces user is a member of
  const { data: userWorkspaces } = useQuery({
    queryKey: ['userWorkspaces', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workspace_members')
        .select(`workspace_id, role, workspaces:workspace_id(${WORKSPACE_FIELDS})`)
        .eq('user_id', user!.id)
        .overrideTypes<Array<{ workspace_id: string; role: string; workspaces: Workspace }>, { merge: false }>();

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
    refetchOnWindowFocus: true,
  });

  const { data: trackedPromptCount = 0 } = useQuery({
    queryKey: ['tracked_prompt_count', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return 0;
      const { count, error } = await supabase
        .from('tracked_prompts')
        .select('id', { count: 'exact', head: true })
        .eq('workspace_id', workspace.id);

      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!workspace?.id,
  });

  const { data: insightsCount = 0 } = useQuery({
    queryKey: ['sidebar_insights_count', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return 0;

      const [recommendationsResult, auditResult] = await Promise.all([
        supabase
          .from('workspace_insight_recommendations')
          .select('recommendation_key, status, is_removed')
          .eq('workspace_id', workspace.id),
        supabase
          .from('workspace_ai_analyses')
          .select('workspace_id')
          .eq('workspace_id', workspace.id)
          .maybeSingle(),
      ]);

      if (recommendationsResult.error) throw recommendationsResult.error;
      if (auditResult.error) throw auditResult.error;

      const hasAudit = Boolean(auditResult.data);
      return (recommendationsResult.data ?? []).filter((row) => {
        if (row.is_removed) return false;
        if (row.status === 'completed') return false;
        if (row.recommendation_key === 'ai-site-audit' && hasAudit) return false;
        return true;
      }).length;
    },
    enabled: !!workspace?.id,
  });

  // If user was removed from active workspace, switch to another
  useEffect(() => {
    if (!userWorkspaces || !workspace) return;
    if (profile?.onboarding_completed === false) return;
    const stillMember = userWorkspaces.some(wm => wm.workspace_id === workspace.id);
    if (!stillMember && userWorkspaces.length > 0) {
      handleSwitchWorkspace(userWorkspaces[0].workspaces);
    }
  }, [userWorkspaces, workspace, profile?.onboarding_completed]);

  const handleSwitchWorkspace = async (targetWorkspace: Workspace) => {
    if (targetWorkspace.id === workspace?.id) {
      setIsWorkspaceDropdownOpen(false);
      return;
    }

    if (viewingAs) {
      // View as switches in memory only; the viewed User's profile row never changes.
      useAuthStore.getState().setWorkspace(targetWorkspace);
    } else {
      // Switching can leave an Awaiting payment workspace, which isn't onboarded yet.
      const { error } = await supabase
        .from('profiles')
        .update({ workspace_id: targetWorkspace.id, onboarding_completed: true })
        .eq('id', user!.id);

      if (error) {
        toast.error('Failed to switch workspace');
        return;
      }

      // Refresh auth store
      await useAuthStore.getState().fetchProfile(user!.id);
    }
    setIsWorkspaceDropdownOpen(false);
    queryClient.invalidateQueries();
    navigate(useAuthStore.getState().workspace?.state === 'awaiting_payment' ? '/onboarding/payment' : '/overview');
  };

  const handleCreateWorkspace = () => {
    if (!user) return;
    setIsWorkspaceDropdownOpen(false);

    // Onboarding step 1 creates the workspace once it has the brand domain.
    sessionStorage.setItem(
      'lima_onboarding_context',
      JSON.stringify({
        canExitOnboarding: Boolean(workspace?.id),
        previousWorkspaceId: workspace?.id ?? null,
        createNew: true,
      })
    );

    navigate('/onboarding/step-1');
  };

  const handleSignOut = async () => {
    setIsWorkspaceDropdownOpen(false);
    await signOut();
    navigate('/login');
  };

  const getInitial = () => {
    if (profile?.first_name) return profile.first_name[0].toUpperCase();
    return 'U';
  };

  const showAvatar = hasValidAvatar(profile?.avatar_url);

  return (
    <nav 
      className={`application-nav w-nav ${effectiveCollapsed ? 'is-collapsed' : ''}`}
    >
      <div className="nav-container">
        {/* Mobile Header */}
        <div className="top-nav-bar is-responsive">
          <Link to="/overview" className="brand w-nav-brand">
            <img
              src="/images/lima-logomark-1_1lima-logomark-1.avif"
              loading="lazy"
              width={44}
              alt="Lima shimmering logo"
              className="nav-logo"
            />
            {!effectiveCollapsed && (
              <img
                src="/images/lima-wordmark.svg"
                loading="lazy"
                width={54}
                alt="Lima logo (wordmark)"
                className="nav-logo"
              />
            )}
          </Link>
          <div className="responsive-nav-actions">
            <button
              className="mobile-account-button"
              onClick={() => {
                onOpenAccount?.();
                setIsMobileMenuOpen(false);
              }}
              aria-label="Open account"
              type="button"
            >
              <div className="user-avatar is-small">
                {showAvatar && profile?.avatar_url ? (
                  <img src={profile.avatar_url} alt="Avatar" className="avatar-image" />
                ) : (
                  <div className="initial">{getInitial()}</div>
                )}
              </div>
            </button>

            <button 
              className={`menu-button w-nav-button ${isMobileMenuOpen ? 'w--open' : ''}`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
              type="button"
            >
              <div className="menu-line"></div>
              <div className="menu-line-middle"></div>
              <div className="menu-line-bottom"></div>
            </button>
          </div>
        </div>

        <div className={`nav-menu w-nav-menu ${isMobileMenuOpen ? 'is-open' : ''}`}>
          {/* Desktop Header */}
          <div className="top-nav-bar is-desktop">
            <div className="brand-expand-wrapper">
              <Link to="/overview" className="brand w-nav-brand nav-logo-link">
                <img
                  src="/images/lima-logomark-1_1lima-logomark-1.avif"
                  loading="lazy"
                  width={44}
                  alt="Lima shimmering logo"
                  className="nav-logo"
                />
                <span className={`nav-wordmark-wrap ${showExpandedContent && !effectiveCollapsed ? 'is-visible' : ''}`}>
                  <img
                    src="/images/lima-wordmark.svg"
                    loading="lazy"
                    width={54}
                    alt="Lima logo (wordmark)"
                    className="nav-logo nav-wordmark"
                  />
                </span>
              </Link>
              {effectiveCollapsed && (
                <button
                  className="nav-expand-button-overlay"
                  onClick={() => setIsCollapsed(false)}
                  title="Expand sidebar"
                >
                  <PanelOpenIcon />
                </button>
              )}
            </div>
            {!effectiveCollapsed && (
              <button
                className="nav-collapse-button"
                onClick={() => setIsCollapsed(true)}
                title="Collapse sidebar"
              >
                <PanelCloseIcon />
              </button>
            )}
          </div>

          {/* Main Navigation */}
          <aside className="middle-nav-bar">
            <NavLink
              to="/overview"
              className={({ isActive }) =>
                `nav-menu-link w-inline-block ${isActive ? 'w--current' : ''} ${effectiveCollapsed ? 'is-collapsed' : ''}`
              }
              title={effectiveCollapsed ? 'Overview' : undefined}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <div className="svg-icon-code w-embed">
                <LayoutGrid size={20} />
              </div>
              <span className={`nav-link-label ${showExpandedContent ? 'is-visible' : ''}`}>Overview</span>
              {effectiveCollapsed && <span className="nav-tooltip">Overview</span>}
            </NavLink>

            <NavLink
              to="/prompts"
              className={({ isActive }) =>
                `nav-menu-link w-inline-block ${isActive ? 'w--current' : ''} ${effectiveCollapsed ? 'is-collapsed' : ''}`
              }
              title={effectiveCollapsed ? 'Prompts' : undefined}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <div className="svg-icon-code w-embed">
                <SquareChartGantt size={20} />
              </div>
              <span className={`nav-link-label ${showExpandedContent ? 'is-visible' : ''}`}>Prompts</span>
              {effectiveCollapsed && <span className="nav-tooltip">Prompts</span>}
            </NavLink>

            <NavLink
              to="/competitors"
              className={({ isActive }) =>
                `nav-menu-link w-inline-block ${isActive ? 'w--current' : ''} ${effectiveCollapsed ? 'is-collapsed' : ''}`
              }
              title={effectiveCollapsed ? 'Competitors' : undefined}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <div className="svg-icon-code w-embed">
                <CompetitorsNavIcon />
              </div>
              <span className={`nav-link-label ${showExpandedContent ? 'is-visible' : ''}`}>Competitors</span>
              {effectiveCollapsed && <span className="nav-tooltip">Competitors</span>}
            </NavLink>

            <NavLink
              to="/mentions"
              className={({ isActive }) =>
                `nav-menu-link w-inline-block ${isActive ? 'w--current' : ''} ${effectiveCollapsed ? 'is-collapsed' : ''}`
              }
              title={effectiveCollapsed ? 'Mentions' : undefined}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <div className="svg-icon-code w-embed">
                <MessageSquareQuote size={20} />
              </div>
              <span className={`nav-link-label ${showExpandedContent ? 'is-visible' : ''}`}>Mentions</span>
              {effectiveCollapsed && <span className="nav-tooltip">Mentions</span>}
            </NavLink>

            <NavLink
              to="/searches"
              className={({ isActive }) =>
                `nav-menu-link w-inline-block ${isActive ? 'w--current' : ''} ${effectiveCollapsed ? 'is-collapsed' : ''}`
              }
              title={effectiveCollapsed ? 'Searches' : undefined}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <div className="svg-icon-code w-embed">
                <Search size={20} />
              </div>
              <span className={`nav-link-label ${showExpandedContent ? 'is-visible' : ''}`}>Searches</span>
              {effectiveCollapsed && <span className="nav-tooltip">Searches</span>}
            </NavLink>

            <NavLink
              to="/insights"
              className={({ isActive }) =>
                `nav-menu-link w-inline-block ${isActive ? 'w--current' : ''} ${effectiveCollapsed ? 'is-collapsed' : ''}`
              }
              title={effectiveCollapsed ? 'Insights' : undefined}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <div className="svg-icon-code w-embed">
                <Sparkles size={20} />
              </div>
              {showExpandedContent ? (
                <span className="nav-link-label is-visible">
                  Insights <span className="new-span sidebar-insights-count">{insightsCount}</span>
                </span>
              ) : (
                <span className="nav-link-label">Insights</span>
              )}
              {effectiveCollapsed && <span className="nav-tooltip">Insights</span>}
            </NavLink>

            {showExpandedContent && <div className="nav-section-label">FREE TOOLS</div>}

            <NavLink
              to="/audit"
              className={({ isActive }) =>
                `nav-menu-link w-inline-block ${isActive ? 'w--current' : ''} ${effectiveCollapsed ? 'is-collapsed' : ''}`
              }
              title={effectiveCollapsed ? 'AI Site Audit' : undefined}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <div className="svg-icon-code w-embed">
                <Bot size={20} />
              </div>
              <span className={`nav-link-label ${showExpandedContent ? 'is-visible' : ''}`}>AI Site Audit</span>
              {effectiveCollapsed && <span className="nav-tooltip">AI Site Audit</span>}
            </NavLink>

            <div className="spacer-medium"></div>
          </aside>

          {/* Lower Navigation */}
          <div className={`lower-nav-bar ${effectiveCollapsed ? 'is-collapsed' : ''}`}>
            {showFounderCallLink && (
              <a
                href={FOUNDER_CALL_URL}
                className={`nav-menu-link w-inline-block ${effectiveCollapsed ? 'is-collapsed' : ''}`}
                title={effectiveCollapsed ? 'Book a call' : undefined}
                onClick={(event) => {
                  event.preventDefault();
                  openFounderCall();
                  setIsMobileMenuOpen(false);
                }}
              >
                <div className="svg-icon-code w-embed">
                  <CalendarDays size={20} />
                </div>
                <span className={`nav-link-label ${showExpandedContent ? 'is-visible' : ''}`}>Book a call</span>
                {effectiveCollapsed && <span className="nav-tooltip">Book a call</span>}
              </a>
            )}
            <a
              href="#"
              className={`nav-menu-link w-inline-block ${effectiveCollapsed ? 'is-collapsed' : ''}`}
              title={effectiveCollapsed ? 'Help & Support' : undefined}
              onClick={(event) => {
                event.preventDefault();
                openCrispChat();
                setIsMobileMenuOpen(false);
              }}
            >
              <div className="svg-icon-code w-embed">
                <LifeBuoy size={20} />
              </div>
              <span className={`nav-link-label ${showExpandedContent ? 'is-visible' : ''}`}>Help & Support</span>
              {effectiveCollapsed && <span className="nav-tooltip">Help & Support</span>}
            </a>

            {/* Plan Limits Box */}
            {showExpandedContent && (
              <PlanLimits
                workspace={workspace}
                promptUsed={trackedPromptCount}
                onUpgrade={() => setIsMobileMenuOpen(false)}
              />
            )}

            <div className="spacer-xsmall"></div>

            {/* Workspace Switcher */}
            <div className="workspace-switcher" ref={workspaceDropdownRef}>
              <div
                className={`workspace-panel ${effectiveCollapsed ? 'is-collapsed' : ''}`}
                onClick={() => setIsWorkspaceDropdownOpen(!isWorkspaceDropdownOpen)}
                style={{ cursor: 'pointer' }}
                title={effectiveCollapsed ? workspace?.name : undefined}
              >
                {showExpandedContent && (
                  <>
                    <div className="credit-flex">
                      <div className="user-name">
                        {workspace?.name || 'My Workspace'}
                      </div>
                      {workspace?.brand_url && (
                        <div className="text-size-tiny text-weight-medium" style={{ color: 'var(--slate-brown)' }}>
                          {workspace.brand_url}
                        </div>
                      )}
                    </div>
                    <div className="dd-arrows">
                      <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={isWorkspaceDropdownOpen ? { transform: 'rotate(180deg)', transition: 'transform 0.2s' } : { transition: 'transform 0.2s' }}>
                        <path d="m18 15-6-6-6 6"/>
                      </svg>
                    </div>
                  </>
                )}
                {!showExpandedContent && (
                  <div className="workspace-icon-collapsed">
                    {(workspace?.name || 'W')[0].toUpperCase()}
                  </div>
                )}
              </div>

              {isWorkspaceDropdownOpen && (
                <div className={`workspace-dropdown ${effectiveCollapsed ? 'is-collapsed' : ''}`}>
                  <div className="workspace-dropdown-list">
                    {userWorkspaces?.map((wm) => (
                      <div
                        key={wm.workspace_id}
                        className={`workspace-dropdown-item ${wm.workspace_id === workspace?.id ? 'is-active' : ''}`}
                        onClick={() => handleSwitchWorkspace(wm.workspaces)}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', minWidth: 0 }}>
                          <div className="text-size-small text-weight-medium">{wm.workspaces.name}</div>
                          {wm.workspaces.state === 'awaiting_payment' && (
                            <span className="status-pill is-pending">Unpaid</span>
                          )}
                        </div>
                        {['owner', 'admin'].includes(wm.role) && (
                          <div
                            className="workspace-settings-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSettingsWorkspace(
                                wm.workspace_id === workspace?.id
                                  ? null
                                  : wm.workspaces
                              );
                              setIsWorkspaceDropdownOpen(false);
                              setIsWorkspaceSettingsOpen(true);
                            }}
                            title="Workspace settings"
                          >
                            <Settings size={16} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="workspace-dropdown-divider"></div>
                  <div
                    className="workspace-dropdown-item is-add"
                    onClick={handleCreateWorkspace}
                  >
                    <Plus size={16} strokeWidth={2} />
                    <div className="text-size-small text-weight-medium">Add new workspace</div>
                  </div>
                  <div className="workspace-dropdown-divider"></div>
                  <div
                    className="workspace-dropdown-item is-logout"
                    onClick={handleSignOut}
                  >
                    <LogOut size={16} strokeWidth={2} />
                    <div className="text-size-small text-weight-medium">Logout</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <WorkspaceSettingsModal
        isOpen={isWorkspaceSettingsOpen}
        onClose={() => { setIsWorkspaceSettingsOpen(false); setSettingsWorkspace(null); }}
        workspaceOverride={settingsWorkspace}
      />
    </nav>
  );
}
